import { useState } from 'react';
import {
  IonContent,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonSpinner,
  IonRefresher,
  IonRefresherContent,
} from '@ionic/react';
import type { RefresherCustomEvent } from '@ionic/react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { GET_MATCH } from '../../../graphql/queries/getMatch';
import type { GetMatchQuery, GetMatchQueryVariables } from '../../../types/graphql-generated';
import { MatchPlayerCard } from './components/MatchPlayerCard';
import { MatchEventRow } from './components/MatchEventRow';
import { MatchLegend } from './components/MatchLegend';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';
import { SendFeedbackModal } from '../../components/SendFeedbackModal';
import { Button } from '@/components/ui/Button';
import { ContentSectionHeader } from '@/components/ui/ContentSectionHeader';

type TeamSegment = 'home' | 'away';

interface MatchViewProps {
  // Lets other sections (e.g. the admin area) show the match within their own stack.
  backHref?: string;
  headerVariant?: 'main' | 'admin';
}

const MatchView: React.FC<MatchViewProps> = ({ backHref = '/app/main/competition', headerVariant = 'main' }) => {
  const matchId = useParams<'matchId'>().matchId!;
  const [selectedTeam, setSelectedTeam] = useState<TeamSegment>('home');
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const { data, loading, error, refetch } = useQuery<GetMatchQuery, GetMatchQueryVariables>(GET_MATCH, {
    variables: { matchId },
  });

  const onRefresh = async (ev: RefresherCustomEvent) => {
    try {
      await refetch();
    } finally {
      ev.detail.complete();
    }
  };

  const match = data?.getMatch;

  // Filter match players by team and starter position
  const getTeamPlayers = (teamId: string, starterPosition: boolean) => {
    if (!match) return [];
    return match.matchPlayers.filter(
      (mp: typeof match.matchPlayers[number]) => mp.team.id === teamId && mp.starterPosition === starterPosition
    );
  };

  const currentTeamId = selectedTeam === 'home' ? match?.homeTeam.id : match?.awayTeam.id;
  const starters = currentTeamId ? getTeamPlayers(currentTeamId, true) : [];
  const substitutes = currentTeamId ? getTeamPlayers(currentTeamId, false) : [];

  // A player's team in this match, keyed by player id, so each event can show
  // the crest (or short name) of the team the player belonged to.
  const teamByPlayerId = new Map(
    (match?.matchPlayers ?? []).map((mp) => [mp.player.id, mp.team])
  );

  return (
    <IonPage>
      <AppSectionHeader variant={headerVariant}>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton
              defaultHref={backHref}
              text=""
              aria-label="Volver"
            />
          </IonButtons>
          <IonTitle className="ml-4 min-w-0">
            {match
              ? `${match.homeTeam.shortName || match.homeTeam.nameShow} vs ${match.awayTeam.shortName || match.awayTeam.nameShow}`
              : 'Partido'}
          </IonTitle>
        </IonToolbar>
      </AppSectionHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={onRefresh}>
          <IonRefresherContent />
        </IonRefresher>
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
            <IonSpinner />
          </div>
        )}

        {error && (
          <div style={{ padding: '16px', textAlign: 'center', color: 'red' }}>
            Error al cargar el partido: {error.message}
          </div>
        )}

        {match && (
          <div className="flex flex-col gap-3.5 p-3.5">
            {/* Scoreboard */}
            <div
              className="flex items-center justify-center gap-4 rounded-2xl p-4 text-white shadow-[0_8px_22px_rgba(12,32,80,0.18)]"
              style={{ background: 'linear-gradient(135deg, #0a2a5b, #1a3d70)' }}
            >
              <div className="flex min-w-0 flex-1 justify-center">
                {match.homeTeam.shield && (
                  <img
                    src={match.homeTeam.shield}
                    alt={match.homeTeam.nameShow}
                    className="h-11 w-11 object-contain"
                  />
                )}
              </div>

              <div className="shrink-0 text-center" data-testid="match-score">
                <div className="flex items-center gap-3">
                  <span className="font-heading" style={{ fontSize: '32px', fontWeight: 800, lineHeight: 1 }}>
                    {match.status === 'finished' ? match.goalsHome : '-'}
                  </span>
                  <span style={{ color: '#8fa2c4', fontSize: '20px' }}>
                    {match.status === 'finished' ? '-' : 'vs'}
                  </span>
                  <span className="font-heading" style={{ fontSize: '32px', fontWeight: 800, lineHeight: 1 }}>
                    {match.status === 'finished' ? match.goalsAway : '-'}
                  </span>
                </div>
                {match.status === 'finished' && (
                  <span
                    className="mt-2 inline-block rounded-full px-2.5 py-[3px] text-[10px] font-extrabold tracking-wide"
                    style={{ background: '#f3e2ac', color: '#0a2a5b' }}
                  >
                    FINALIZADO
                  </span>
                )}
              </div>

              <div className="flex min-w-0 flex-1 justify-center">
                {match.awayTeam.shield && (
                  <img
                    src={match.awayTeam.shield}
                    alt={match.awayTeam.nameShow}
                    className="h-11 w-11 object-contain"
                  />
                )}
              </div>
            </div>

            {/* Team selector */}
            <IonSegment
              value={selectedTeam}
              onIonChange={(e) => setSelectedTeam(e.detail.value as TeamSegment)}
            >
              <IonSegmentButton value="home">
                <IonLabel>{match.homeTeam.nameShow}</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="away">
                <IonLabel>{match.awayTeam.nameShow}</IonLabel>
              </IonSegmentButton>
            </IonSegment>

            {/* Starters */}
            <ContentSectionHeader label="Titulares" count={starters.length} barColor="#c8a951" />
            <div className="overflow-hidden rounded-2xl border border-[#e8ebf1] bg-white shadow-[0_1px_2px_rgba(20,30,60,0.04)]">
              {starters.length > 0 ? (
                starters.map((mp: typeof starters[number]) => <MatchPlayerCard key={mp.id} matchPlayer={mp} />)
              ) : (
                <div className="px-4 py-5 text-center text-sm text-slate-400">
                  No hay titulares disponibles
                </div>
              )}
            </div>

            {/* Substitutes */}
            <ContentSectionHeader label="Suplentes" count={substitutes.length} barColor="#9aa3b2" />
            <div className="overflow-hidden rounded-2xl border border-[#e8ebf1] bg-white shadow-[0_1px_2px_rgba(20,30,60,0.04)]">
              {substitutes.length > 0 ? (
                substitutes.map((mp: typeof substitutes[number]) => <MatchPlayerCard key={mp.id} matchPlayer={mp} />)
              ) : (
                <div className="px-4 py-5 text-center text-sm text-slate-400">
                  No hay suplentes disponibles
                </div>
              )}
            </div>

            {/* Events section */}
            {match.events.length > 0 && (
              <>
                <ContentSectionHeader label="Eventos" barColor="#2f6bd8" />
                <div className="rounded-2xl border border-[#e8ebf1] bg-white px-3.5 shadow-[0_1px_2px_rgba(20,30,60,0.04)]">
                  {match.events.map((event: typeof match.events[number]) => (
                    <MatchEventRow
                      key={event.id}
                      event={event}
                      team={teamByPlayerId.get(event.player.id)}
                    />
                  ))}
                </div>
              </>
            )}

            {/* Legend */}
            <ContentSectionHeader label="Leyenda" barColor="#9aa3b2" />
            <MatchLegend />

            {/* Send feedback */}
            <Button variant="outline" fullWidth onClick={() => setFeedbackOpen(true)} className="mt-1">
              Enviar comentario
            </Button>
          </div>
        )}
      </IonContent>

      <SendFeedbackModal
        isOpen={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        entityType="MATCH"
        entityId={matchId}
      />
    </IonPage>
  );
};

export default MatchView;
